import { useMemo, useState, useCallback, useEffect } from "react";
import { useAccount } from "wagmi";
import { useDispatch, useSelector } from "react-redux";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import useSanction from "@hooks/useSanction";
import { USDT } from "@config/contractsConfig";
import { CONNECT_WALLET, CRYPTANIUM_VAULT } from "@constants/analytics";
import {
  toFixedWithoutRounding,
  MAX_FRACTION_DIGITS,
  isNumber,
} from "@constants/common";
import { EVENTS } from "@constants/reduxEvents";
import useDappConfig from "@hooks/useDappConfig";
import useTokenBalance from "@hooks/useTokenBalance";
import useExchangeRate from "@hooks/useExchangeRate";
import useCryptaniumVaultAllowance from "@hooks/useCryptaniumVaultAllowance";
import useCryptaniumVaultExchangeRate from "@hooks/useCryptaniumVaultExchangeRate";
import useCryptaniumVaultDepositLimit from "@hooks/useCryptaniumVaultDepositLimit";
import { updateUserData } from "@store";
import { isFloat, toStringPrecision, trackEvent, emitReduxEvent } from "utils";
import { CRYPTANIUM_VAULT_STRINGS } from "./constants";
import useCryptaniumVaultData from "@hooks/useCryptaniumVaultData";

export const useXgUsdVault = () => {
  const {
    isLoadingAPYData,
    formattedApy,
    formattedTvl,
    isLoadingTotalStaked,
    formattedETHStaked,
  } = useCryptaniumVaultData();

  const { isSanctioned } = useSanction();

  const vaultHeaderData = useMemo(
    () => ({
      isLoadingAPYData,
      isLoadingTotalStaked,
      totalStaked:
        formattedTvl && formattedTvl !== "-" ? `$${formattedTvl}` : "-",
      totalStakedEth: formattedETHStaked,
      apyValue: formattedApy,
      apyLabel: CRYPTANIUM_VAULT_STRINGS.HEADER.STATS.APY,
      totalStakedLabel: CRYPTANIUM_VAULT_STRINGS.HEADER.STATS.TVL,
      title: CRYPTANIUM_VAULT_STRINGS.HEADER.TITLE,
      logo: {
        src: "/assets/vaults/cryptanium.svg",
        label: "xgUSD Logo",
      },
      auditedUrl:
        "https://kerneldao.gitbook.io/kernel/getting-started/gain/cryptanium-gain#audits",
    }),
    [
      isLoadingAPYData,
      isLoadingTotalStaked,
      formattedTvl,
      formattedETHStaked,
      formattedApy,
    ]
  );

  const tabsData = useMemo(
    () => CRYPTANIUM_VAULT_STRINGS.SECTIONS.DEPOSIT_WITHDRAW.TABS,
    []
  );

  const portfolioConfig = useMemo(
    () => ({
      tabs: CRYPTANIUM_VAULT_STRINGS.SECTIONS.PORTFOLIO.TABS,
      points: CRYPTANIUM_VAULT_STRINGS.SECTIONS.PORTFOLIO.POINTS,
      overviewData: CRYPTANIUM_VAULT_STRINGS.SECTIONS.OVERVIEW.DATA,
      aprReports: CRYPTANIUM_VAULT_STRINGS.SECTIONS.PORTFOLIO.APR_REPORTS,
    }),
    []
  );

  return {
    vaultHeaderData,
    tabsData,
    portfolioConfig,
    isSanctioned,
    constants: CRYPTANIUM_VAULT_STRINGS,
  };
};

// Cryptanium Vault specific deposit logic (only deposit, no withdraw)
export const useXgUsdDeposit = () => {
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");

  const { isConnected, address } = useAccount();
  const { isCryptaniumVaultSupportedChain, cryptaniumVaultConfig } =
    useDappConfig();
  const { erc20Allowance, minDepositAmount } = useCryptaniumVaultAllowance();
  const { tokensBalances, xTokenCryptaniumVaultBalance } = useTokenBalance();

  const { selectedERC20Cryptanium, referralId, cryptaniumFormResetNonce } =
    useSelector((state: any) => state.user);
  const dispatch = useDispatch();
  const { openConnectModal } = useConnectModal();

  const tokenBalance = useMemo(() => {
    if (selectedERC20Cryptanium === cryptaniumVaultConfig.xToken.symbol)
      return xTokenCryptaniumVaultBalance;
    return tokensBalances[selectedERC20Cryptanium] || 0;
  }, [
    tokensBalances,
    selectedERC20Cryptanium,
    cryptaniumVaultConfig.xToken.symbol,
    xTokenCryptaniumVaultBalance,
  ]);

  const conversionDataRsEth = useExchangeRate("cryptaniumvault");
  const cryptaniumVaultExchangeRate = useCryptaniumVaultExchangeRate();

  const conversionsRsETH = useMemo(() => {
    const conversionToERC20 = conversionDataRsEth?.exchangeRate;
    return {
      toERC20: conversionToERC20 || 0,
      fromERC20: conversionToERC20 ? 1 / conversionToERC20 : 0,
    };
  }, [conversionDataRsEth?.exchangeRate]);

  const getXgUSDExchangeRate = useCallback(() => {
    const xgUSDRate =
      selectedERC20Cryptanium === USDT ? 1 : Number(conversionsRsETH?.toERC20);
    return Number(cryptaniumVaultExchangeRate?.toERC20) * xgUSDRate;
  }, [
    cryptaniumVaultExchangeRate?.toERC20,
    conversionsRsETH?.toERC20,
    selectedERC20Cryptanium,
  ]);

  const exchangeRate = getXgUSDExchangeRate();

  const { depositLimit, maxDeposit } = useCryptaniumVaultDepositLimit();

  // Calculate the display value: 1 xgUSD = X USDT (same pattern as sbUSD)
  const valueInUsdt = cryptaniumVaultExchangeRate?.toERC20
    ? toFixedWithoutRounding(
        Number(cryptaniumVaultExchangeRate.toERC20).toString(),
        4
      )
    : undefined;

  const checkInput = useCallback(
    (value = amount.trim()) => {
      if (!isConnected) return "";
      let error = "";
      if (!+value || Number.isNaN(+value)) {
        error =
          "This field can't be blank or zero. Please provide valid input.";
      } else if (Number(value) > tokenBalance) {
        error = `You can't deposit more than available balance in your wallet.`;
      } else if (Number(value) < minDepositAmount) {
        error = `Cryptanium vault has a minimum deposit of ${minDepositAmount} ${selectedERC20Cryptanium}. Please try with a higher amount.`;
      } else if (Number(value) > Number(depositLimit)) {
        error = `Cryptanium vault allows up to ${maxDeposit} ${selectedERC20Cryptanium}. You can deposit up to ${toFixedWithoutRounding(
          Number(depositLimit).toString(),
          4
        )} ${selectedERC20Cryptanium}`;
      }
      return error;
    },
    [
      amount,
      isConnected,
      tokenBalance,
      minDepositAmount,
      depositLimit,
      selectedERC20Cryptanium,
      maxDeposit,
    ]
  );

  useEffect(() => {
    if (amount !== "") {
      const error = checkInput(amount);
      setError(error);
    }
  }, [tokenBalance, address, amount, checkInput]);

  const isApproved = useMemo(() => {
    return (
      erc20Allowance &&
      Number(erc20Allowance) &&
      Number(erc20Allowance) >= Number(amount)
    );
  }, [erc20Allowance, amount]);

  useEffect(() => {
    setError("");
  }, [selectedERC20Cryptanium]);

  // Clear amount & error when Services reports a successful tx
  useEffect(() => {
    if (cryptaniumFormResetNonce) {
      setAmount("");
      setError("");
    }
  }, [cryptaniumFormResetNonce]);

  const convertedAmount = useMemo(() => {
    const hgRate = getXgUSDExchangeRate();
    if (amount && !isNaN(Number(amount))) {
      const value = Number(amount) / (hgRate > 0 ? hgRate : 1);
      const formatedValue =
        value && isFloat(value)
          ? toFixedWithoutRounding(
              toStringPrecision(value),
              MAX_FRACTION_DIGITS
            )
          : toStringPrecision(value);
      if (!Number.isNaN(+formatedValue)) {
        return formatedValue;
      } else {
        return "0";
      }
    } else {
      return "0";
    }
  }, [amount, getXgUSDExchangeRate]);

  const handleClick = useCallback(
    (inputAmount?: string) => {
      const amountToUse = inputAmount || amount;

      if (!isConnected) {
        openConnectModal?.();
        trackEvent(CONNECT_WALLET, {
          type: CRYPTANIUM_VAULT.DEPOSIT_CTA,
        });
      } else if (!isCryptaniumVaultSupportedChain) {
        emitReduxEvent(EVENTS.SWITCH_NETWORK);
      } else {
        if (isApproved) {
          if (!amountToUse) {
            setError(
              "This field can't be blank or zero. Please provide valid input."
            );
            return;
          }

          emitReduxEvent(EVENTS.HANDLE_CRYPTANIUM_VAULT_DEPOSIT, {
            amount: amountToUse,
            referralId,
          });

          setAmount("");
        } else {
          emitReduxEvent(EVENTS.HANDLE_CRYPTANIUM_VAULT_APPROVE, {
            amount: amountToUse,
          });
        }
      }
    },
    [
      isConnected,
      isCryptaniumVaultSupportedChain,
      openConnectModal,
      isApproved,
      amount,
      referralId,
    ]
  );

  const handleChange = (value: string) => {
    const val = value.trim().replace(/,/g, "");
    if (isNumber(val) || val === "") {
      if (val.indexOf(".") === -1) {
        setAmount(val);
      } else {
        if (val.length - (val.indexOf(".") + 1) <= MAX_FRACTION_DIGITS) {
          setAmount(val === "." ? "0." : val);
        }
      }
    }
  };

  const handleMaxClick = useCallback(() => {
    const maxAmount = Math.min(tokenBalance, Number(depositLimit));
    handleChange(toFixedWithoutRounding(maxAmount.toString(), 4));
  }, [tokenBalance, depositLimit]);

  const handleTokenSelect = (key: string) => {
    dispatch(
      updateUserData({
        ["selectedERC20Cryptanium"]: key,
      })
    );
  };

  return {
    amount,
    setAmount,
    error,
    setError,
    tokenBalance,
    isConnected,
    isApproved,
    convertedAmount,
    exchangeRate,
    selectedERC20Vault: selectedERC20Cryptanium,
    vaultConfig: cryptaniumVaultConfig,
    xTokenVaultBalance: xTokenCryptaniumVaultBalance,
    conversionsDataXgUsd: cryptaniumVaultExchangeRate,
    valueInUsdt,
    handleClick,
    handleChange,
    handleMaxClick,
    handleTokenSelect,
    isCryptaniumVaultSupportedChain,
  };
};

// Cryptanium Vault doesn't have withdraw functionality, but we provide a placeholder
export const useXgUsdWithdraw = () => {
  return null; // CryptaniumVault only has deposit
};
